import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  DialogContent,
  Chip,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import api from "../../axiosInstance";
import { toast } from "react-toastify";

const ACCENT = "#17479e";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`;

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const groupByDate = (slots) => {
  const map = new Map();
  slots.forEach((slot) => {
    const key = slot.specificDate ?? "—";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(slot);
  });
  return Array.from(map.entries()).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
};

function MentorAvailabilityListDialog({ open, mentorRow, onClose }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !mentorRow) return;
    setLoading(true);
    setError("");
    api
      .get("/admin/mentors/availability", { params: { mentorId: mentorRow.mentorId } })
      .then((res) => setSlots(res.data?.data?.oneOffSlots ?? []))
      .catch((err) => {
        const message =
          err.response?.data?.errorMessage || "Failed to load availabilities.";
        setError(message);
        toast.error(message);
        setSlots([]);
      })
      .finally(() => setLoading(false));
  }, [open, mentorRow]);

  const groups = useMemo(() => groupByDate(slots), [slots]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2.2,
          background: HEADER_GRADIENT,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              background: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EventAvailableIcon sx={{ color: "#fff", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem" }}>
              Availabilities
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.78rem" }}>
              {mentorRow?.mentorName ?? "—"}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ color: "#fff", fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : error ? (
          <Typography sx={{ color: "error.main", fontSize: "0.85rem", textAlign: "center", py: 2 }}>
            {error}
          </Typography>
        ) : groups.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 3 }}
          >
            No availability set for this mentor yet.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {groups.map(([date, dateSlots]) => (
              <Box
                key={date}
                sx={{
                  borderRadius: "14px",
                  border: "1px solid #edf0f5",
                  boxShadow: "0 2px 10px rgba(23,71,158,0.06)",
                  overflow: "hidden",
                  transition: "box-shadow 0.15s ease",
                  "&:hover": { boxShadow: "0 6px 20px rgba(23,71,158,0.12)" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    px: 2,
                    py: 1.3,
                    borderLeft: `4px solid ${ACCENT}`,
                    background: "#f8faff",
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: "9px",
                      background: "#eef3fc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <CalendarTodayIcon sx={{ fontSize: 16, color: ACCENT }} />
                  </Box>
                  <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: "#26324a", flex: 1 }}>
                    {formatDate(date)}
                  </Typography>
                  <Chip
                    label={`${dateSlots.length} slot${dateSlots.length > 1 ? "s" : ""}`}
                    size="small"
                    sx={{
                      bgcolor: "#fff",
                      border: `1px solid ${ACCENT}55`,
                      color: ACCENT,
                      fontWeight: 700,
                      fontSize: "0.68rem",
                    }}
                  />
                </Box>
                <Box sx={{ px: 2, py: 0.5 }}>
                  {dateSlots.map((slot, i) => (
                    <Box
                      key={slot.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 0.8,
                        py: 1.1,
                        borderBottom: i < dateSlots.length - 1 ? "1px dashed #edf0f5" : "none",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <AccessTimeIcon sx={{ fontSize: 15, color: "#9aa4b2" }} />
                        <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#37474f" }}>
                          {slot.startTime}
                        </Typography>
                        {slot.sessionTypeName && (
                          <Chip
                            label={slot.sessionTypeName}
                            size="small"
                            sx={{
                              bgcolor: "#eef3fc",
                              color: ACCENT,
                              fontWeight: 600,
                              height: 22,
                              fontSize: "0.68rem",
                            }}
                          />
                        )}
                      </Box>
                      {slot.sessionAmount != null && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.1,
                            px: 1.1,
                            py: 0.3,
                            borderRadius: "20px",
                            background: "#e8f5e9",
                            color: "#2e7d32",
                            fontWeight: 700,
                            fontSize: "0.78rem",
                          }}
                        >
                          <CurrencyRupeeIcon sx={{ fontSize: 13 }} />
                          {slot.sessionAmount}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default MentorAvailabilityListDialog;
